import { z } from 'zod';
import type { MoneyJson } from '../money/money';
import { moneySchema } from './common.schemas';

/**
 * Warranty and repeat-repair flags on a work order, the claim against a garage, and who bears the cost
 * (gitaumoses4/chukua#175). A flag is derived from the order's parts lines and the truck's history, never typed; the
 * human steps (a verdict on a flag, a claim, a cost decision) are append-only evidence.
 */

/** Hard-coded in a CHECK on `work_order_flag_decisions`, so a new flag is a migration. */
export const WORK_ORDER_FLAGS = ['repeat_repair', 'warranty'] as const;
export type WorkOrderFlag = (typeof WORK_ORDER_FLAGS)[number];

export const FLAG_DECISIONS = ['confirmed', 'dismissed'] as const;
export type FlagDecision = (typeof FLAG_DECISIONS)[number];

/** `raised → submitted → accepted | rejected | withdrawn`, and `raised → withdrawn`; `warranty-claim-transition.policy.ts` is the one table. */
export const WARRANTY_CLAIM_STATUSES = ['raised', 'submitted', 'accepted', 'rejected', 'withdrawn'] as const;
export type WarrantyClaimStatus = (typeof WARRANTY_CLAIM_STATUSES)[number];

export const COST_BEARERS = ['owner', 'garage', 'absorbed'] as const;
export type CostBearer = (typeof COST_BEARERS)[number];

const reason = z.string().trim().min(1).max(500);

/** `POST /work-orders/:id/flag-decisions` — the verdict on one trigger; `sourceWorkOrderId` is the earlier order that raised it. */
export const recordFlagDecisionInputSchema = z
  .object({ flag: z.enum(WORK_ORDER_FLAGS), sourceWorkOrderId: z.uuid(), decision: z.enum(FLAG_DECISIONS), reason })
  .strict();
export type RecordFlagDecisionInput = z.infer<typeof recordFlagDecisionInputSchema>;

/** `POST /work-orders/:id/warranty-claims` — the earlier order whose warranty is being claimed on. */
export const raiseWarrantyClaimInputSchema = z.object({ sourceWorkOrderId: z.uuid() }).strict();
export type RaiseWarrantyClaimInput = z.infer<typeof raiseWarrantyClaimInputSchema>;

/** `POST /warranty-claims/:claimId/accept` — what was recovered is optional, in the order's own currency. */
export const acceptWarrantyClaimInputSchema = z
  .object({
    reason,
    recoveredAmount: moneySchema.refine((money) => money.amountMinor >= 0, { message: 'An amount cannot be negative', path: ['amountMinor'] }).optional(),
  })
  .strict();
export type AcceptWarrantyClaimInput = z.infer<typeof acceptWarrantyClaimInputSchema>;

/** `POST /warranty-claims/:claimId/{reject|withdraw}`. */
export const warrantyClaimReasonInputSchema = z.object({ reason }).strict();
export type WarrantyClaimReasonInput = z.infer<typeof warrantyClaimReasonInputSchema>;

/** `POST /work-orders/:id/cost-decisions` — `replacesId` is the current decision when changing one. */
export const recordCostDecisionInputSchema = z.object({ bearer: z.enum(COST_BEARERS), reason, replacesId: z.uuid().optional() }).strict();
export type RecordCostDecisionInput = z.infer<typeof recordCostDecisionInputSchema>;

/** One earlier order that makes this order a repeat repair or a warranty case. */
export interface WorkOrderFlagTriggerView {
  flag: WorkOrderFlag;
  sourceWorkOrderId: string;
  sourceSequenceNumber: number;
  /** The part types both orders fitted. */
  partTypes: { id: string; name: string }[];
  sourceFittedAt: string;
  /** Warranty only: who did the earlier work and until when it was covered. */
  garageId: string | null;
  garageName: string | null;
  warrantyEndsAt: string | null;
  /** The newest verdict on this trigger; null until someone decides. A dismissed trigger is shown but does not count. */
  decision: FlagDecision | null;
  decisionReason: string | null;
  decidedById: string | null;
  decidedAt: string | null;
}

export interface WorkOrderFlagsView {
  /** The flags that count: those with a trigger nobody has dismissed. */
  flags: WorkOrderFlag[];
  /** Every trigger, dismissed ones included, so the screen can show why a flag is not raised. */
  triggers: WorkOrderFlagTriggerView[];
}

export interface WarrantyClaimEventView {
  id: string;
  status: WarrantyClaimStatus;
  reason: string | null;
  recoveredAmount: MoneyJson | null;
  actorId: string;
  createdAt: string;
}

export interface WarrantyClaimView {
  id: string;
  workOrderId: string;
  sourceWorkOrderId: string;
  garageId: string;
  garageName: string | null;
  raisedById: string;
  createdAt: string;
  /** The newest event's status. */
  status: WarrantyClaimStatus;
  events: WarrantyClaimEventView[];
}

export interface CostDecisionView {
  id: string;
  workOrderId: string;
  bearer: CostBearer;
  reason: string;
  replacesId: string | null;
  decidedById: string;
  createdAt: string;
}

export interface CostDecisionsView {
  /** The decision nothing replaces; null until one is made. */
  current: CostDecisionView | null;
  /** Every decision, oldest first. */
  history: CostDecisionView[];
  /** False when the current decision names the garage but neither a counting warranty flag nor an accepted claim remains (a later parts-line correction can do this). */
  basisHolds: boolean;
}

/** Filters on the work order list (`flag`, `claimStatus`, `costBearer`); each adds one "none of these" value. */
export const WORK_ORDER_FLAG_FILTERS = [...WORK_ORDER_FLAGS, 'unflagged'] as const;
export const WORK_ORDER_CLAIM_FILTERS = [...WARRANTY_CLAIM_STATUSES, 'none'] as const;
export const WORK_ORDER_COST_BEARER_FILTERS = [...COST_BEARERS, 'undecided'] as const;
